import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCountdown } from "@/hooks/useCountdown";
import { callCancelSubmit } from "@/lib/functions";
import type { PendingSubmit } from "@/lib/types";
import { ParticipantBadge } from "./ParticipantBadge";

interface Props {
  sessionId: string;
  pendingSubmit: PendingSubmit;
  myParticipantId: string;
}

const RING_R = 92;
const RING_C = 2 * Math.PI * RING_R;
const TOTAL_MS = 5_000;

export const CountdownOverlay = ({
  sessionId,
  pendingSubmit,
  myParticipantId,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { secondsLeft } = useCountdown(pendingSubmit);
  const [cancelling, setCancelling] = useState(false);
  const [tooLate, setTooLate] = useState(false);

  // Continuous progress (0 → 1 over 5s) for the ring stroke.
  const elapsed = Math.max(0, Math.min(TOTAL_MS, TOTAL_MS - (pendingSubmit.expiresAt - Date.now())));
  const progress = Math.min(1, elapsed / TOTAL_MS);
  const dashOffset = RING_C * (1 - progress);

  const triggererId = pendingSubmit.triggeredBy === "phone" ? null : null;
  void triggererId;

  const isMineTrigger = false; // The triggeredBy is the role ("phone"/"tablet"/"qr"), not the participantId.
  void myParticipantId;
  void isMineTrigger;

  const onCancel = async (): Promise<void> => {
    setCancelling(true);
    try {
      const res = await callCancelSubmit({ sessionId, pendingId: pendingSubmit.id });
      if (!res.data.cancelled) setTooLate(true);
    } catch {
      setTooLate(true);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("countdown.ariaLabel", "Envoi en cours")}
      className="fixed inset-0 z-[90] bg-ink/85 backdrop-blur-sm flex flex-col items-center justify-center px-6 animate-fade-in"
    >
      <div className="relative w-56 h-56 flex items-center justify-center">
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 200 200" aria-hidden="true">
          <circle cx="100" cy="100" r={RING_R} stroke="rgba(255,255,255,0.15)" strokeWidth="8" fill="none" />
          <circle
            cx="100"
            cy="100"
            r={RING_R}
            stroke="#c8553d"
            strokeWidth="8"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={RING_C}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 100ms linear" }}
          />
        </svg>
        {secondsLeft > 0 ? (
          <span
            className="display-font text-7xl font-semibold text-white tabular-nums"
            aria-live="polite"
          >
            {secondsLeft}
          </span>
        ) : (
          <span
            className="block w-12 h-12 rounded-full border-4 border-white/30 border-t-white animate-spin"
            role="status"
            aria-label={t("submit.frozen", "Envoi en cours…")}
          />
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 text-white/85">
        <ParticipantBadge
          participantId={pendingSubmit.id}
          displayName={pendingSubmit.triggeredBy}
          size="xs"
        />
        <span className="text-sm">
          {t("countdown.triggeredBy", "Envoi déclenché par")}{" "}
          <span className="text-white font-medium">{pendingSubmit.triggeredBy}</span>
        </span>
      </div>

      <p className="mt-2 text-white/70 text-center max-w-xs">
        {secondsLeft > 0
          ? t("countdown.kitchenSoon", "Envoi en cuisine dans quelques secondes…")
          : t("submit.frozen", "Envoi en cours…")}
      </p>

      <div className="mt-10 w-full max-w-xs">
        {!tooLate ? (
          <button
            type="button"
            disabled={cancelling || secondsLeft === 0}
            onClick={onCancel}
            className="w-full bg-white text-ink rounded-pill py-4 px-5 font-semibold disabled:opacity-50 transition-transform active:scale-95"
          >
            {cancelling ? t("countdown.cancelling", "Annulation…") : t("countdown.cancel", "Annuler")}
          </button>
        ) : (
          <div className="text-center">
            <p className="text-2xl mb-2">🍳</p>
            <p className="text-white/85">{t("countdown.tooLate", "Trop tard, la commande est partie en cuisine.")}</p>
          </div>
        )}
      </div>
    </div>
  );
};
